/**
 * Dashboard Redux Slice
 * Manages dashboard state including filters, chart data, and UI state
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { 
  DashboardState, 
  ChartData, 
  DashboardFilters,
  MetricTotal,
  DashboardType
} from '../pages/dashboard/types/dashboard.types';
import dashboardApi, { DashboardApiService } from '../pages/dashboard/services/dashboardApi';

// Initial state
const initialState: DashboardState = {
  // Data
  info: null,
  chartData: null,
  scorecards: null,
  medicalDirectorData: null,
  
  // UI State
  filters: {
    facilityId: null,
    dateRange: {
      start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days ago (default for patient counts)
      end: new Date().toISOString(), // today
    },
    dashboardType: 'info', // Default to Patient Counts dashboard
  },
  totalMetrics: [],
  allMetricsTotal: 0,
  allAverageTotal: 0,
  showScorecards: false,
  
  // Loading states
  loading: {
    info: false,
    chartData: false,
    scorecards: false,
    medicalDirector: false,
    export: false,
  },
  
  // Error states
  error: {
    info: null,
    chartData: null,
    scorecards: null,
    medicalDirector: null,
    export: null,
  },
  
  // UI interaction state
  activeModal: null,
  currentBroadcastIndex: 0,
  
  // Track if this is initial load to apply server defaults
  isInitialLoad: true,
};

// Async thunks for API calls
export const fetchDashboardInfo = createAsyncThunk(
  'dashboard/fetchInfo',
  async () => {
    const response = await dashboardApi.getDashboardInfo();
    return response;
  }
);

export const fetchChartData = createAsyncThunk(
  'dashboard/fetchChartData',
  async (filters: DashboardFilters) => {
    const payload = DashboardApiService.createFiltersPayload(filters);
    const response = await dashboardApi.getEncounterRange(payload);
    return response;
  }
);

export const fetchScorecards = createAsyncThunk(
  'dashboard/fetchScorecards',
  async (filters: DashboardFilters) => {
    const payload = DashboardApiService.createFiltersPayload(filters);
    const response = await dashboardApi.getScorecards(payload);
    return response;
  }
);

export const exportReport = createAsyncThunk(
  'dashboard/exportReport',
  async (filters: DashboardFilters) => {
    const payload = {
      ...DashboardApiService.createFiltersPayload(filters),
      generated_from_dashboard: true,
    };
    
    let blob;
    try {
      blob = await dashboardApi.exportEncounterReport(payload);
    } catch (error) {
      throw error;
    }
    
    // Trigger download
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'EncountersReport.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return true;
  }
);

export const updateBroadcastLog = createAsyncThunk(
  'dashboard/updateBroadcastLog',
  async (params: { messageId: number; hasUserSeen: boolean; shouldCloseOrNavigate?: boolean }) => {
    await dashboardApi.updateBroadcastLog(params.messageId, params.hasUserSeen);
    return params;
  }
);

export const fetchMedicalDirectorDashboard = createAsyncThunk(
  'dashboard/fetchMedicalDirectorDashboard',
  async (params: { filters: DashboardFilters; daysRun?: number }) => {
    const payload = {
      ...DashboardApiService.createFiltersPayload(params.filters),
      ...(params.daysRun && { days_run: params.daysRun })
    };
    const response = await dashboardApi.getMedicalDirectorDashboard(payload);
    return response;
  }
);

// Helper function to calculate metric totals
function calculateMetricTotals(chartData: ChartData, dateRange: { start: string; end: string }): {
  totalMetrics: MetricTotal[];
  allMetricsTotal: number;
  allAverageTotal: number;
} {
  const totalMetrics: MetricTotal[] = [];
  let allMetricsTotal = 0;

  // Calculate days in range (parse ISO strings back to Date objects for calculation)
  const oneDay = 24 * 60 * 60 * 1000;
  const startDate = new Date(dateRange.start);
  const endDate = new Date(dateRange.end);
  const daysInRange = Math.ceil((endDate.getTime() - startDate.getTime() + oneDay) / oneDay);
  
  // Process each metric
  Object.keys(chartData).forEach(key => {
    if (key === 'x_axis_label' || key === 'utc_offset') return;
    
    const metric = chartData[key] as any;
    if (metric?.metric_values) {
      const total = metric.metric_values.reduce((sum: number, point: any) => sum + point.value, 0);
      const average = Math.round((total * 100) / daysInRange) / 100;
      
      totalMetrics.push({
        label: key,
        total,
        average,
      });
      
      allMetricsTotal += total;
    }
  });
  
  const allAverageTotal = Math.round(
    totalMetrics.reduce((sum, metric) => sum + metric.average, 0) * 100
  ) / 100;
  
  return { totalMetrics, allMetricsTotal, allAverageTotal };
}

// Dashboard slice
const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    // Update filters
    setFilters: (state, action: PayloadAction<Partial<DashboardFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    
    // Set facility from global context
    setFacilityFromContext: (state, action: PayloadAction<number>) => {
      state.filters.facilityId = action.payload;
    },
    
    // Set date range based on default from API
    setDefaultDateRange: (state, action: PayloadAction<number>) => {
      const daysBack = action.payload;
      state.filters.dateRange.start = new Date(Date.now() - daysBack * 24 * 60 * 60 * 1000).toISOString();
      state.filters.dateRange.end = new Date().toISOString();
    },
    
    // Set dashboard type and adjust date range appropriately
    setDashboardType: (state, action: PayloadAction<DashboardType>) => {
      const prevType = state.filters.dashboardType;
      state.filters.dashboardType = action.payload;
      
      // Set appropriate default date ranges when switching dashboard types
      if (prevType !== action.payload) {
        if (action.payload === 'info') {
          // Patient Counts dashboard - default to 7 days
          state.filters.dateRange.start = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
          state.filters.dateRange.end = new Date().toISOString();
        } else if (action.payload === 'med_dash') {
          // Medical Director dashboard - default to 90 days
          state.filters.dateRange.start = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString();
          state.filters.dateRange.end = new Date().toISOString();
        } else if (action.payload === 'quality') {
          // Inclusive start and end dates produce exactly 90 calendar dates.
          state.filters.dateRange.start = new Date(Date.now() - 89 * 24 * 60 * 60 * 1000).toISOString();
          state.filters.dateRange.end = new Date().toISOString();
        }
      }
    },
    
    // Modal management
    setActiveModal: (state, action: PayloadAction<'broadcast' | 'labs' | null>) => {
      state.activeModal = action.payload;
    },
    
    setBroadcastIndex: (state, action: PayloadAction<number>) => {
      state.currentBroadcastIndex = action.payload;
    },
    
    // Show broadcast modal with first unread message
    showBroadcastModal: (state) => {
      const unreadMessages = state.info?.broadcast_messages?.filter(msg => !msg.has_user_seen) || [];
      if (unreadMessages.length > 0) {
        state.activeModal = 'broadcast';
        state.currentBroadcastIndex = 0;
      }
    },
    
    // Show labs modal if there are overdue labs
    showLabsModal: (state) => {
      if (state.info?.overdue_labs && state.info.overdue_labs.lab_count > 0) {
        state.activeModal = 'labs';
      }
    },
    
    // Close broadcast modal
    closeBroadcastModal: (state) => {
      if (state.activeModal === 'broadcast') {
        state.activeModal = null;
        state.currentBroadcastIndex = 0;
      }
    },
    
    // Close labs modal
    closeLabsModal: (state) => {
      if (state.activeModal === 'labs') {
        state.activeModal = null;
      }
    },
    
    // Move to next broadcast message
    nextBroadcastMessage: (state) => {
      const unreadMessages = state.info?.broadcast_messages?.filter(msg => !msg.has_user_seen) || [];
      if (state.currentBroadcastIndex < unreadMessages.length - 1) {
        state.currentBroadcastIndex += 1;
      }
    },
    
    // Clear errors
    clearErrors: (state) => {
      state.error = {
        info: null,
        chartData: null,
        scorecards: null,
        medicalDirector: null,
        export: null,
      };
    },
  },
  extraReducers: (builder) => {
    // Dashboard info
    builder
      .addCase(fetchDashboardInfo.pending, (state) => {
        state.loading.info = true;
        state.error.info = null;
      })
      .addCase(fetchDashboardInfo.fulfilled, (state, action) => {
        state.loading.info = false;
        state.info = action.payload;

        // Facility selection is now handled by the facilities slice
        // which listens to tenantContext.facilityId

        // Set default date range from server response only on initial load
        // This ensures patient counts dashboard defaults to 7 days (from server default_date_range: 6)
        // while preserving user selections on subsequent loads
        if (state.isInitialLoad && state.filters.dashboardType === 'info') {
          const daysBack = action.payload.default_date_range + 1; // API returns 6, we need 7 days
          state.filters.dateRange.start = new Date(Date.now() - daysBack * 24 * 60 * 60 * 1000).toISOString();
          state.filters.dateRange.end = new Date().toISOString();
          state.isInitialLoad = false; // Mark as no longer initial load
        }
      })
      .addCase(fetchDashboardInfo.rejected, (state, action) => {
        state.loading.info = false;
        state.error.info = action.error.message || 'Failed to fetch dashboard info';
      });
    
    // Chart data
    builder
      .addCase(fetchChartData.pending, (state) => {
        state.loading.chartData = true;
        state.error.chartData = null;
      })
      .addCase(fetchChartData.fulfilled, (state, action) => {
        state.loading.chartData = false;
        state.chartData = action.payload;
        
        // Calculate metric totals
        const totals = calculateMetricTotals(action.payload, state.filters.dateRange);
        state.totalMetrics = totals.totalMetrics;
        state.allMetricsTotal = totals.allMetricsTotal;
        state.allAverageTotal = totals.allAverageTotal;
      })
      .addCase(fetchChartData.rejected, (state, action) => {
        state.loading.chartData = false;
        state.error.chartData = action.error.message || 'Failed to fetch chart data';
      });
    
    // Scorecards
    builder
      .addCase(fetchScorecards.pending, (state) => {
        state.loading.scorecards = true;
        state.error.scorecards = null;
      })
      .addCase(fetchScorecards.fulfilled, (state, action) => {
        state.loading.scorecards = false;
        state.scorecards = action.payload;
        state.showScorecards = action.payload.scores && action.payload.scores.length > 0;
      })
      .addCase(fetchScorecards.rejected, (state, action) => {
        state.loading.scorecards = false;
        state.error.scorecards = action.error.message || 'Failed to fetch scorecards';
      });
    
    // Export
    builder
      .addCase(exportReport.pending, (state) => {
        state.loading.export = true;
        state.error.export = null;
      })
      .addCase(exportReport.fulfilled, (state) => {
        state.loading.export = false;
      })
      .addCase(exportReport.rejected, (state, action) => {
        state.loading.export = false;
        state.error.export = action.error.message || 'Failed to export report';
      });
    
    // Broadcast log update
    builder
      .addCase(updateBroadcastLog.fulfilled, () => {
        // Don't update Redux state here - it causes re-renders on unmounted components
        // The message won't show again anyway because the backend returns has_user_seen: true
        // on next page load
      });
    
    // Medical Director dashboard
    builder
      .addCase(fetchMedicalDirectorDashboard.pending, (state) => {
        state.loading.medicalDirector = true;
        state.error.medicalDirector = null;
      })
      .addCase(fetchMedicalDirectorDashboard.fulfilled, (state, action) => {
        state.loading.medicalDirector = false;
        state.medicalDirectorData = action.payload;
      })
      .addCase(fetchMedicalDirectorDashboard.rejected, (state, action) => {
        state.loading.medicalDirector = false;
        state.error.medicalDirector = action.error.message || 'Failed to fetch medical director dashboard';
      });
  },
});

export const {
  setFilters,
  setFacilityFromContext,
  setDefaultDateRange,
  setDashboardType,
  setActiveModal,
  setBroadcastIndex,
  showBroadcastModal,
  showLabsModal,
  closeBroadcastModal,
  closeLabsModal,
  nextBroadcastMessage,
  clearErrors,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
